import { ReactNode } from "react";
import { motion } from "framer-motion";

interface CardProps {
  children: ReactNode;
  className?: string;
  hover?: boolean;
  padding?: "none" | "sm" | "md" | "lg";
  variant?: "glass" | "solid" | "gradient";
}

export default function Card({
  children,
  className = "",
  hover = true,
  padding = "md",
  variant = "glass",
}: CardProps) {
  const paddingClasses = {
    none: "",
    sm: "p-4",
    md: "p-6",
    lg: "p-8",
  };

  const variantClasses = {
    glass:
      "bg-white/70 dark:bg-gray-900/70 backdrop-blur-xl border border-white/30 dark:border-gray-700/40 shadow-glass",
    solid:
      "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-soft",
    gradient:
      "bg-gradient-to-br from-white/80 to-primary-50/40 dark:from-gray-900/80 dark:to-primary-950/30 backdrop-blur-xl border border-primary-100/40 dark:border-primary-800/30 shadow-primary-soft",
  };

  const baseClasses = `rounded-2xl transition-all duration-300 ${variantClasses[variant]} ${paddingClasses[padding]} ${className}`;

  const hoverShadow =
    variant === "gradient"
      ? "0 20px 48px rgba(79, 70, 229, 0.15)"
      : variant === "glass"
        ? "0 20px 48px rgba(0, 0, 0, 0.08)"
        : "0 20px 40px rgba(0, 0, 0, 0.1)";

  const cardVariants = {
    initial: { y: 20, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    hover: hover
      ? {
          y: -4,
          boxShadow: hoverShadow,
          transition: { duration: 0.25, ease: "easeOut" },
        }
      : {},
  };

  return (
    <motion.div
      variants={cardVariants}
      initial="initial"
      whileInView="animate"
      whileHover="hover"
      viewport={{ once: true }}
      className={baseClasses}
    >
      {children}
    </motion.div>
  );
}
